import type { ReactNode } from 'react';

import './icone.css';

/* Ícones do design system: um traço só, `viewBox` de 24, mesma espessura e
   mesmos cantos arredondados em todos — a barra tem de parecer desenhada, não
   improvisada. Vieram inteiros da `Casca`, sem mudança de forma.

   `aria-hidden`: o ícone nunca nomeia nada; quem nomeia é o rótulo ao lado.
   Sem dependência de pacote: cinco caminhos custam menos que um Phosphor
   inteiro no cache do aparelho.

   `width`/`height` em `1em`, não em 24: é o tamanho que a spec §7 pede, e é
   também o que sobra onde a folha de estilo vizinha (`icone.css`) não chegar.
   O `viewBox` continua em 24 — ele é a grade do desenho, não o tamanho
   pintado. */
export function Icone({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <svg
      className={className ? `ds-icone ${className}` : 'ds-icone'}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const ICONES = {
  // Hoje: folhinha do dia, com o dia marcado.
  hoje: (
    <Icone>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <circle cx="12" cy="15.5" r="1.6" fill="currentColor" stroke="none" />
    </Icone>
  ),
  // Pessoas: duas figuras.
  pessoas: (
    <Icone>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.2 2.7-5.2 6-5.2s6 2 6 5.2" />
      <path d="M16.5 5.4a3.2 3.2 0 0 1 0 6M18 14.4c2 .7 3.5 2.5 3.5 4.6" />
    </Icone>
  ),
  // Território: mapa dobrado.
  territorio: (
    <Icone>
      <path d="M9 4.5 3.5 6.8v12.7L9 17.2l6 2.3 5.5-2.3V4.5L15 6.8 9 4.5Z" />
      <path d="M9 4.5v12.7M15 6.8v12.7" />
    </Icone>
  ),
  // Relatório: colunas do mês.
  relatorio: (
    <Icone>
      <path d="M4 20V10M10 20V4M16 20v-7M4 20h17" />
    </Icone>
  ),
  // Ajustes: controles deslizantes.
  ajustes: (
    <Icone>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2.2" />
      <circle cx="8" cy="17" r="2.2" />
    </Icone>
  ),
  // Mais ações: as reticências do iOS, usadas pela `Linha`.
  mais: (
    <Icone>
      <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </Icone>
  ),

  /* Os seis resultados da folha Registrar (pranchas 02.1–02.2). Cada um
     desenha o que aconteceu na porta, não uma abstração: o rótulo já diz a
     palavra, e o ícone existe para achar o chip certo de relance, com o
     polegar a caminho. Todos no mesmo `viewBox` de 24 e no mesmo traço dos
     seis de cima. */

  // Não estava: porta fechada.
  naoEstava: (
    <Icone>
      <path d="M6 3.5h12v17H6z" />
      <circle cx="14.6" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </Icone>
  ),
  // Conversamos: balão de fala.
  conversamos: (
    <Icone>
      <path d="M4 6.5a2.5 2.5 0 0 1 2.5-2.5h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4H6.5A2.5 2.5 0 0 1 4 13.5Z" />
    </Icone>
  ),
  // Sem interesse: o círculo cortado.
  semInteresse: (
    <Icone>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m6.4 6.4 11.2 11.2" />
    </Icone>
  ),
  // Publicação: folheto com dobra.
  publicacao: (
    <Icone>
      <path d="M6 3.5h7.5L18 8v12.5H6z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9 12.5h6M9 16h4" />
    </Icone>
  ),
  // Revisita: a seta que volta.
  revisita: (
    <Icone>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4.5V10h-5.5" />
    </Icone>
  ),
  // Estudo: livro aberto.
  estudo: (
    <Icone>
      <path d="M3.5 5.5h5.2c1.8 0 3.3 1 3.3 2.3v11c0-1.1-1.3-2-3-2H3.5Z" />
      <path d="M20.5 5.5h-5.2c-1.8 0-3.3 1-3.3 2.3v11c0-1.1 1.3-2 3-2h5.5Z" />
    </Icone>
  ),

  // Cadeado fechado: a tela trancada (prancha 12.1).
  cadeado: (
    <Icone>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </Icone>
  ),
  /* Recarregar: a falha que se tenta de novo (prancha 14.3). Duas setas, e
     não a única de `revisita`: as duas ações são circulares, e num app que
     desenha "revisitar" o mesmo traço para "tentar de novo" faria o usuário
     ler a palavra para saber qual é qual — que é justamente o trabalho que o
     ícone existe para poupar. */
  recarregar: (
    <Icone>
      <path d="M3.5 12a8.5 8.5 0 0 1 14.5-6" />
      <path d="M20.5 12a8.5 8.5 0 0 1-14.5 6" />
      <path d="M18.5 2.5V6H15M5.5 21.5V18H9" />
    </Icone>
  ),
} as const;
